import type { LucideIcon } from "lucide-react";
import {
  PenLine,
  Sparkles,
  Camera,
  Tag,
  Calendar,
  Building2,
  Heart,
  Users,
  Bell,
  Megaphone,
  Gift,
  Landmark,
  CalendarHeart,
  UserCircle,
  ShieldCheck,
  Briefcase,
  HelpCircle,
  Settings,
  Search,
  Store,
  Wand2,
  Target,
  Ticket,
  UsersRound,
  MessageSquare,
  Globe,
  Mail,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════
// Feature registry — single source of truth for the Lab dashboard.
//
// Phase 1E.1 ships this as the dashboard's data model so feature-labs
// land as content updates instead of structural ones:
//   - Commit 1 (Foundation): only `identity-board` + `nostalgia` are
//     `live`. Everything else is `coming-soon` (route path is the
//     planned final URL but the page itself isn't built yet, so the
//     card is rendered disabled).
//   - Commit 2 (User feature-labs): flip status to `live` as each
//     feature-lab section lands. No structural changes to the
//     dashboard component.
//   - Commit 3 (Admin): same pattern for the `admin` entries — they
//     stay hidden until the user has admin role, and individually
//     flip to `live` as their admin labs ship.
// ═══════════════════════════════════════════════════════════════════

export type FeatureStatus = "live" | "coming-soon";

export type FeatureCategory =
  | "identity"
  | "community"
  | "utsav"
  | "rewards"
  | "heritage"
  | "platform"
  | "admin";

export interface FeatureCard {
  /** Stable identifier for analytics + flag overrides. */
  id: string;
  /** Card title shown in the dashboard grid. */
  title: string;
  /** One-line description. ~60 chars max so it fits the card. */
  description: string;
  /** Lucide icon component rendered inside the gradient tile. */
  icon: LucideIcon;
  /** Routing target. Even `coming-soon` cards declare the eventual
   *  path so flipping status is a one-character change. */
  path: string;
  /** Whether the feature-lab page actually exists yet. */
  status: FeatureStatus;
  /** Grouping for the dashboard's sections. */
  category: FeatureCategory;
  /** Backend phase that introduced this surface — helps the user
   *  triage which lab is most recently shipped. Optional. */
  phase?: string;
  /** If true, hidden from non-admin users. Shown only when
   *  `useRole().isAdmin` is true. */
  adminOnly?: boolean;
}

// ─── Identity & profile ─────────────────────────────────────────────

const IDENTITY: FeatureCard[] = [
  {
    id: "identity-board",
    title: "Identity Board",
    description:
      "Build your identity, browse matches, send waves, manage mutual connections.",
    icon: Sparkles,
    path: "/lab/identity-board",
    status: "live",
    category: "identity",
  },
  {
    id: "conversations",
    title: "Messages",
    description:
      "The inbox after a mutual wave: each conversation's last message, unread counts, and the one check an app polls.",
    icon: MessageSquare,
    path: "/lab/conversations",
    status: "live",
    category: "identity",
  },
  {
    id: "nostalgia-memories",
    title: "Memories — the cue library",
    description:
      "Tap remember on a shared cue; write only if you have words. Two counts, deliberately far apart.",
    icon: Sparkles,
    path: "/lab/nostalgia/memories",
    status: "live",
    category: "community",
  },
  {
    id: "nostalgia",
    title: "Nostalgia (Yaadein)",
    description:
      "Curated memory boards — TV shows, music, snacks, festivals from your era.",
    icon: Camera,
    path: "/lab/nostalgia",
    status: "live",
    category: "identity",
  },
  {
    id: "interests",
    title: "Custom Interests",
    description:
      "Propose new touchstones / spiritual practices / sewa types; see your proposals.",
    icon: Tag,
    path: "/lab/interests",
    status: "live",
    category: "identity",
    phase: "1A",
  },
];

// ─── Utsav (sacred worship layer) ───────────────────────────────────

const UTSAV: FeatureCard[] = [
  {
    id: "utsav-calendar",
    title: "Festival Calendar",
    description:
      "Today's festivals, upcoming dates, full 2026–2027 Indian festival calendar.",
    icon: CalendarHeart,
    path: "/lab/utsav",
    status: "live",
    category: "utsav",
    phase: "1B.4",
  },
  {
    id: "institutions",
    title: "Institutions",
    description:
      "Register a temple/gurudwara/mosque, manage schedules, photos, admins, followers.",
    icon: Building2,
    path: "/lab/institutions",
    status: "live",
    category: "utsav",
    phase: "1B.2 / 1B.3 / 1B.8",
  },
  {
    id: "worship-events",
    title: "Worship Events",
    description:
      "Create worship-realm events, post updates, collect post-event feedback.",
    icon: Landmark,
    path: "/lab/worship-events",
    status: "live",
    category: "utsav",
    phase: "1B.5",
  },
  {
    id: "volunteers",
    title: "Volunteers",
    description:
      "Browse / create opportunities, sign up, manage signups, track your history.",
    icon: Heart,
    path: "/lab/volunteers",
    status: "live",
    category: "utsav",
    phase: "1B.6",
  },
  {
    id: "event-organizers",
    title: "Event Organizers",
    description:
      "Register an organizer profile, invite co-organizers, track verification.",
    icon: Users,
    path: "/lab/event-organizers",
    status: "live",
    category: "utsav",
    phase: "1C.1",
  },
  {
    id: "organizer-directory",
    title: "Organizer Directory",
    description:
      "Browse researched organizers and claim yours — your page goes live pre-filled.",
    icon: Landmark,
    path: "/lab/event-organizers/directory",
    status: "live",
    category: "utsav",
    phase: "E6.3",
  },
];

// ─── Community & platform ───────────────────────────────────────────

const COMMUNITY: FeatureCard[] = [
  {
    id: "waitlist",
    title: "Waitlist",
    description:
      "Who gave an email before the product existed. Admin only, with a CSV for sending invites.",
    icon: Mail,
    path: "/lab/waitlist",
    status: "live",
    category: "admin",
  },
  {
    id: "circles",
    title: "Circles",
    description:
      "Friend groups — who you go out with. Shareable join links with no recipient recorded, and silent declines.",
    icon: UsersRound,
    path: "/lab/circles",
    status: "live",
    category: "community",
  },
  {
    id: "provider-messages",
    title: "Provider messages",
    description:
      "The only place a member talks to a business — and the lead pipeline the shop desk reads from.",
    icon: MessageSquare,
    path: "/lab/provider-messages",
    status: "live",
    category: "community",
  },
  {
    id: "public-surface",
    title: "Public surface",
    description:
      "What a stranger reads with no account, beside the lists only you can. Counts and content, never people.",
    icon: Globe,
    path: "/lab/public",
    status: "live",
    category: "platform",
  },
  {
    id: "search",
    title: "Search",
    description:
      "One index over fourteen kinds of content. Matched by spelling and by meaning at once — try a typo, or a request phrased as a need.",
    icon: Search,
    path: "/lab/search",
    status: "live",
    category: "community",
  },
  {
    id: "bazaar",
    title: "The Bazaar",
    description:
      "Haats, shelves near you, and taking a stall as your own business.",
    icon: Store,
    path: "/lab/bazaar",
    status: "live",
    category: "community",
  },
  {
    id: "telemetry",
    title: "Telemetry",
    description:
      "Fire an analytics event, then read the aggregate it moved. Includes what people searched for and did not find.",
    icon: Target,
    path: "/lab/telemetry",
    status: "live",
    category: "community",
  },
  {
    id: "provider-desk",
    title: "Shop desk",
    description:
      "The owner dashboard, reviewed location drafts, campaigns, delivery reports, and catalogue.",
    icon: Store,
    path: "/lab/providers/desk",
    status: "live",
    category: "community",
  },
  {
    id: "board-signals",
    title: "Board signals",
    description:
      "Match and wave counts, the vocabularies a board is built from, and the full nudge lifecycle.",
    icon: Target,
    path: "/lab/identity-board/signals",
    status: "live",
    category: "community",
  },
  {
    id: "institution-admin",
    title: "Institution admin",
    description:
      "The desk for a temple you help run: aarti board, photos, admins, followers and the legal record.",
    icon: Landmark,
    path: "/lab/institutions/admin",
    status: "live",
    category: "community",
  },
  {
    id: "event-desk",
    title: "Organiser desk",
    description:
      "Updates, feedback, tiers, promo codes, attendees, orders, refunds and rescheduling for one event.",
    icon: Ticket,
    path: "/lab/events/desk",
    status: "live",
    category: "community",
  },
  {
    id: "event-seats",
    title: "Seats — queue & passes",
    description:
      "Seeded events with the waitlist and what you already hold on each.",
    icon: Ticket,
    path: "/lab/events/seats",
    status: "live",
    category: "community",
  },
  {
    id: "event-draft",
    title: "Write it like you'd text it",
    description:
      "Paste a WhatsApp message; get a filled-in event draft back.",
    icon: Wand2,
    path: "/lab/events/draft",
    status: "live",
    category: "community",
  },
  {
    id: "providers",
    title: "Providers",
    description:
      "Trust-based service directory — Pandits, nannies, CPAs, cooks. Browse, filter, vouch.",
    icon: Briefcase,
    path: "/lab/providers",
    status: "live",
    category: "community",
  },
  {
    id: "questions",
    title: "Curios (Q&A)",
    description:
      "Community Q&A — ask, answer, @mention providers, upvote, accept the best answer.",
    icon: HelpCircle,
    path: "/lab/questions",
    status: "live",
    category: "community",
  },
  {
    id: "announcements",
    title: "Announcements",
    description:
      "What's happening around you — every card shows WHY it ranks: pinned, scope, affinity boost, language barrier.",
    icon: Megaphone,
    path: "/lab/announcements",
    status: "live",
    category: "community",
  },
  {
    id: "composer",
    title: "Composer",
    description:
      "One box. Classify free text, watch the ruling, submit — it becomes a Voice, a Question, or routes away.",
    icon: PenLine,
    path: "/lab/composer",
    status: "live",
    category: "community",
  },
  {
    id: "voices",
    title: "Voices (Awaaz)",
    description:
      "Community advocacy posts, take a stand, share your story, comment, upvote.",
    icon: Megaphone,
    path: "/lab/voices",
    status: "live",
    category: "community",
  },
  {
    id: "events",
    title: "Events",
    description:
      "Browse community events, RSVP, see updates, create general (non-worship) events.",
    icon: Calendar,
    path: "/lab/events",
    status: "live",
    category: "community",
  },
  {
    id: "connections",
    title: "Connections",
    description: "Follow people, manage your followers, block list.",
    icon: UserCircle,
    path: "/lab/connections",
    status: "live",
    category: "community",
  },
  {
    id: "notifications",
    title: "Notifications",
    description:
      "Inbox, lifecycle markers (delivered/opened/tapped), funnel-event tester.",
    icon: Bell,
    path: "/lab/notifications",
    status: "live",
    category: "platform",
    phase: "1D.1",
  },
  {
    id: "test-member",
    title: "Test member",
    description:
      "Reset first-run or peer to any stage and sign in as it. Two test members can wave and talk. Hidden from every real member.",
    icon: UserCircle,
    path: "/lab/test-member",
    status: "live",
    category: "platform",
  },
  {
    id: "settings",
    title: "Settings & Privacy",
    description:
      "Profile/location visibility + contact-on-match + notification preferences.",
    icon: Settings,
    path: "/lab/settings",
    status: "live",
    category: "platform",
  },
];

// ─── Rewards & heritage ─────────────────────────────────────────────

const REWARDS: FeatureCard[] = [
  {
    id: "yash",
    title: "Yash (Rewards)",
    description:
      "Your balance, transaction history, partner offers, redeem & use coupons.",
    icon: Gift,
    path: "/lab/yash",
    status: "live",
    category: "rewards",
  },
  {
    id: "dharohar",
    title: "Dharohar (Heritage)",
    description:
      "Daily personality / art-form / handicraft card; browse the catalog.",
    icon: Sparkles,
    path: "/lab/dharohar",
    status: "live",
    category: "heritage",
  },
];

// ─── Admin (hidden unless useRole().isAdmin) ────────────────────────

const ADMIN: FeatureCard[] = [
  {
    id: "admin-recruitment",
    title: "Recruitment",
    description:
      "Shelves with demand we cannot serve, and which listings to call.",
    icon: Target,
    path: "/admin/recruitment",
    status: "live",
    category: "admin",
    adminOnly: true,
  },
  {
    id: "admin-verification-queue",
    title: "Verification Queue",
    description:
      "Unified queue for institutions, event-organizers, interest proposals. Approve / reject / request-info.",
    icon: ShieldCheck,
    path: "/admin/verification-queue",
    status: "live",
    category: "admin",
    phase: "1B.3 + 1C.1",
    adminOnly: true,
  },
  {
    id: "admin-announcements",
    title: "Announcements Desk",
    description:
      "Publish, pin, retire. The form enforces affinity-vs-barrier — universal news can never carry a language filter.",
    icon: ShieldCheck,
    path: "/admin/announcements",
    status: "live",
    category: "admin",
    adminOnly: true,
  },
  {
    id: "admin-reports",
    title: "Content Reports",
    description:
      "Community-flagged posts grouped by item, loudest first. Review or dismiss; removal stays separate.",
    icon: ShieldCheck,
    path: "/admin/reports",
    status: "live",
    category: "admin",
    adminOnly: true,
  },
  {
    id: "admin-analytics",
    title: "Analytics",
    description:
      "Notification performance dashboard + engagement funnel drop-off charts.",
    icon: ShieldCheck,
    path: "/admin/analytics",
    status: "live",
    category: "admin",
    phase: "1D.1",
    adminOnly: true,
  },
  {
    id: "admin-interests-queue",
    title: "Interests Queue",
    description: "Review user-proposed interests / touchstones.",
    icon: ShieldCheck,
    path: "/admin/interests-queue",
    status: "live",
    category: "admin",
    phase: "1A",
    adminOnly: true,
  },
  {
    id: "admin-yash-fraud",
    title: "Yash Fraud Queue",
    description: "Review flagged Yash transactions; manual award overrides.",
    icon: ShieldCheck,
    path: "/admin/yash-fraud",
    status: "live",
    category: "admin",
    adminOnly: true,
  },
  {
    id: "admin-dharohar-moderation",
    title: "Dharohar Review",
    description: "Approve or reject each heritage record once, with every source it cites.",
    icon: ShieldCheck,
    path: "/admin/dharohar-moderation",
    status: "live",
    category: "admin",
    adminOnly: true,
  },
];

// ─── Composite export ──────────────────────────────────────────────

export const FEATURES: FeatureCard[] = [
  ...IDENTITY,
  ...UTSAV,
  ...COMMUNITY,
  ...REWARDS,
  ...ADMIN,
];

/** Categories in display order on the dashboard. */
export const CATEGORY_ORDER: FeatureCategory[] = [
  "identity",
  "utsav",
  "community",
  "platform",
  "rewards",
  "heritage",
  "admin",
];

export const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  identity: "Identity & Profile",
  utsav: "Utsav — Sacred Worship Layer",
  community: "Community",
  platform: "Platform",
  rewards: "Rewards",
  heritage: "Heritage",
  admin: "Admin",
};

export const CATEGORY_SUBTITLES: Partial<Record<FeatureCategory, string>> = {
  utsav: "Phase 1B — institutions, festivals, worship events, volunteers",
  platform: "Phase 1D — analytics + funnels",
  admin: "Hidden from non-admin accounts",
};

// ─── Helpers ───────────────────────────────────────────────────────

export function featuresForRole(isAdmin: boolean): FeatureCard[] {
  return FEATURES.filter((f) => (f.adminOnly ? isAdmin : true));
}

export function featuresByCategory(
  isAdmin: boolean,
): Record<FeatureCategory, FeatureCard[]> {
  const visible = featuresForRole(isAdmin);
  const buckets: Record<FeatureCategory, FeatureCard[]> = {
    identity: [],
    utsav: [],
    community: [],
    platform: [],
    rewards: [],
    heritage: [],
    admin: [],
  };
  for (const f of visible) buckets[f.category].push(f);
  return buckets;
}

export function liveFeatures(isAdmin: boolean): FeatureCard[] {
  return featuresForRole(isAdmin).filter((f) => f.status === "live");
}
